import { api, queryString, ApiRequestError } from './client.ts';
import type { Appointment, SlotSuggestion } from '../../shared/types/index.ts';
import i18n from '../i18n/index.ts';
import type {
  CleanerSchedulingProfile,
  CleanerSkill,
  JobSkill,
  ScoredProposal,
} from '../../shared/cleaner-scheduling.ts';
import type {
  AvailabilityProfile,
  AvailabilityTeamProfile,
  AvailabilityException,
  AvailabilityWindow,
  PlanningConflictPage,
} from '../../shared/types-availability.ts';
export async function withAvailabilityConfirmation<T>(
  write: (token?: string) => Promise<T>,
  isCurrent?: () => boolean,
): Promise<T> {
  try {
    return await write();
  } catch (error) {
    if (!(error instanceof ApiRequestError) || error.code !== 'AVAILABILITY_CONFIRMATION_REQUIRED') throw error;
    // An abandoned create must not prompt or POST again from another screen.
    if (isCurrent && !isCurrent()) throw error;
    const token = error.fields.availabilityConfirmation;
    if (!token || !window.confirm(i18n.t('availability.confirmWarning'))) throw error;
    if (isCurrent && !isCurrent()) throw error;
    return write(token);
  }
}
export const planningApi = {
  moveSuggestions: async (id: string) => {
    const visit = await api.getFresh<Appointment>(`/schedule/appointments/${id}`);
    return api.getFresh<{ suggestions: SlotSuggestion[]; warning?: string | null }>(
      `/schedule/suggestions${queryString({ durationMinutes: visit.durationMinutes, frequency: 'one_time', userIds: visit.assignedUserIds.join(','), excludeAppointmentId: id })}`,
    );
  },
  ignore: (key: string) => api.post<void>('/schedule/conflicts/ignore', { key }),
  profile: (userId: string) => api.getFresh<AvailabilityProfile>(`/schedule/availability/profiles/${userId}`),
  saveProfile: (userId: string, body: { revision: number; windows: AvailabilityWindow[]; inherit?: boolean }) =>
    api.put<AvailabilityProfile>(`/schedule/availability/profiles/${userId}`, body),
  teamProfile: async (teamId: string): Promise<AvailabilityProfile> => {
    const p = await api.getFresh<AvailabilityTeamProfile>(`/schedule/availability/teams/${teamId}`);
    return { userId: p.teamId, revision: p.revision, windows: p.windows, inherited: !p.overrideMembers };
  },
  saveTeamProfile: (teamId: string, body: { revision: number; windows: AvailabilityWindow[]; inherit?: boolean }) =>
    api.put<AvailabilityTeamProfile>(`/schedule/availability/teams/${teamId}`, {
      ...body,
      overrideMembers: !body.inherit,
    }),
  conflicts: (params: {
    from: string;
    to: string;
    userId?: string;
    teamId?: string;
    page: number;
    pageSize: number;
    cursor?: string;
  }) => api.getFresh<PlanningConflictPage>(`/schedule/conflicts${queryString(params)}`),
  exceptions: (params: { from: string; to: string; userId?: string; teamId?: string; page?: number }) =>
    api.getFresh<{ items: AvailabilityException[]; total?: number; totalPages?: number }>(
      `/schedule/availability/exceptions${queryString({ ...params, pageSize: 100 })}`,
    ),
  addBlock: (body: {
    id: string;
    userId?: string;
    teamId?: string;
    kind?: 'available' | 'unavailable';
    startsAt: string;
    endsAt: string;
  }) => api.post<AvailabilityException>('/schedule/availability/exceptions', body),
  removeBlock: (block: AvailabilityException) =>
    api.delete<void>(`/schedule/availability/exceptions/${block.id}`, { revision: block.revision }),
};
export function newPlanningId(): string {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  return [...crypto.getRandomValues(new Uint8Array(26))].map((n) => alphabet[n % 32]).join('');
}

/** Auto-scheduling (docs/features/auto-scheduling.md). */
export type CleanerProposal = ScoredProposal & { nthWeek: number | null };
export const autoScheduleApi = {
  profile: (userId: string) =>
    api.getFresh<CleanerSchedulingProfile>(`/schedule/availability/scheduling-profiles/${userId}`),
  saveProfile: (
    userId: string,
    body: {
      revision: number;
      maxJobsPerDay: number | null;
      travelRadiusKm: number | null;
      skills: CleanerSkill[];
      homeAddress?: {
        addressLine1: string;
        city: string | null;
        state: string | null;
        postalCode: string | null;
        countryCode: string | null;
      } | null;
    },
  ) => api.put<CleanerSchedulingProfile>(`/schedule/availability/scheduling-profiles/${userId}`, body),
  suggestions: (params: {
    quoteId: string;
    durationMinutes: number;
    frequency: string;
    from?: string;
    propertyId?: string;
  }) =>
    api.getFresh<{
      items: CleanerProposal[];
      requiredSkill: CleanerSkill | null;
      requiredJobSkills: JobSkill[];
      distanceKind: string;
      uncertain: boolean;
      reservedWeeks: number | null;
    }>(`/schedule/availability/cleaner-suggestions${queryString(params)}`),
};
